import { useQueryClient, type QueryClient } from "@tanstack/react-query"
import { useEffect } from "react"
import type { HqUser } from "./auth"
import { getLocale, storedChoice, useLocale, type Locale } from "./i18n"
import { saveMyMetadata } from "./account-metadata"

/**
 * The language follows the PERSON, not the phone: it is kept on their Medusa admin account
 * (user metadata `hq_locale` + `hq_locale_at`) as well as on the device. Whichever choice was made
 * last wins — choosing Greek on the phone shows Greek on the laptop at its next start, and a choice
 * made on the sign-in page, or one that could not reach the server, is carried to the account at the
 * next start.
 */

type Choice = { locale: Locale; at: number }

function accountChoice(user: HqUser | null): Choice | null {
  const metadata = user?.metadata ?? null
  const locale = metadata?.hq_locale
  if (locale !== "en" && locale !== "el") return null
  return { locale, at: Number(metadata?.hq_locale_at) || 0 }
}

/** Saves the choice on the account. Throws when the server refused, so the caller can say so. */
export async function saveAccountLanguage(queryClient: QueryClient, user: HqUser, locale: Locale, at: number) {
  await saveMyMetadata(queryClient, user, { hq_locale: locale, hq_locale_at: at })
}

/** Keeps the device and the account on the same language, the newer choice winning. */
export function useAccountLanguage(user: HqUser | null) {
  const { setLocale } = useLocale()
  const queryClient = useQueryClient()
  const saved = accountChoice(user)
  useEffect(() => {
    if (!user) return
    const local = storedChoice()
    if (saved && (!local || saved.at > local.at)) {
      // Chosen on another device since: take it up here.
      if (saved.locale !== getLocale()) void setLocale(saved.locale, saved.at)
    } else if (local && (!saved || local.at > saved.at)) {
      // Chosen here first (on the sign-in page, or while the server was out of reach): carry it over.
      void saveAccountLanguage(queryClient, user, local.locale, local.at).catch(() => undefined)
    }
    // A new choice is a new locale or a new time, on either side; nothing else needs a re-run.
  }, [user?.id, saved?.locale, saved?.at]) // eslint-disable-line react-hooks/exhaustive-deps
}

export function useSaveAccountLanguage() {
  const queryClient = useQueryClient()
  return (user: HqUser, locale: Locale, at: number) => saveAccountLanguage(queryClient, user, locale, at)
}
